import React, { useEffect, useState } from "react"
import { useDebounce } from "use-debounce"

import { useGetJiraFieldOptions } from "@/api/useJira"
import { AutoComplete, Select, Spin } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { removeBTags } from "@/molecules/JiraIssueField/helpers"
import { JiraIssueFieldProps } from "@/molecules/JiraIssueField/JiraIssueField.types"

import styles from "../JiraIssueField.module.scss"

export const AutoCompleteField = ({
  field,
  onChange,
  projectId,
  value,
  disabled
}: React.ComponentProps<typeof AutoComplete> &
  JiraIssueFieldProps & { value?: string | string[] }) => {
  const [displayLabel, setDisplayLabel] = useState("")
  const [searchValue, setSearchValue] = useState("")
  const [initialFetchDone, setInitialFetchDone] = useState(false)
  const [debouncedSearchValue] = useDebounce(searchValue, 1000)
  const { t } = useTranslation("translation", {
    keyPrefix: "jiraIssueModal"
  })

  // Determine query: use search input if typing, otherwise use initial value
  // Handle array values (like labels) by taking the first element
  const initialValue = Array.isArray(value) ? value?.[0] : value
  const queryValue = debouncedSearchValue || initialValue

  // Only fetch if: user is searching OR not yet initially fetched
  const shouldFetch = !!debouncedSearchValue || !initialFetchDone
  const isLoadingInitial = !!value && !displayLabel && !searchValue

  const { response, isLoading } = useGetJiraFieldOptions(
    {
      q: queryValue,
      fieldKey: field.key,
      fieldName: field.name,
      projectId: projectId
    },
    {
      enabled: shouldFetch,
      gcTime: 60000,
      staleTime: 0
    }
  )

  // Set displayLabel from initial value fetch result
  useEffect(() => {
    if (isLoadingInitial && response?.data?.[0]) {
      setDisplayLabel(removeBTags(response.data[0].label))
      setInitialFetchDone(true)
    }
  }, [response, isLoadingInitial])

  const onClear = () => {
    setDisplayLabel("")
    setSearchValue("")
    setInitialFetchDone(false)
    onChange?.(undefined, {})
  }

  const options = response?.data || []
  const displayValue = displayLabel || searchValue

  return (
    <Select
      showSearch
      value={displayValue}
      onSearch={setSearchValue}
      allowClear
      onClear={onClear}
      loading={isLoading}
      disabled={(isLoadingInitial && isLoading) || disabled}
      notFoundContent={
        isLoading ? (
          <Spin size="small" className={styles.optionsLoader} />
        ) : (
          t("noResults")
        )
      }
      filterOption={false}
    >
      {options.map((option) => {
        const normalizedLabel = removeBTags(option.label)
        return (
          <Select.Option key={option.value} value={option.value}>
            <div
              onClick={() => {
                setDisplayLabel(normalizedLabel)
                onChange?.(option.value, {
                  label: normalizedLabel,
                  id: option.value
                })
              }}
              dangerouslySetInnerHTML={{ __html: normalizedLabel }}
            />
          </Select.Option>
        )
      })}
    </Select>
  )
}
